import {
  Club,
  ClubCounterAdjustmentRequest,
  ClubCounterAdjustmentResult,
  ClubFinanceEntry,
  ClubFinanceManualEntryRequest,
  ClubFinanceOverview,
  ClubFinancePlayerTrail,
  ClubFinancePlayerPosition,
  ClubFinanceSummary,
  ClubSubscriptionStatus,
  ClubSubscriptionPlan,
  ClubSubscriptionContract,
  ClubSubscriptionInvoice,
  ClubSubscriptionBillingType,
  ClubAnalyticsOverviewPayload,
  ClubRakeAnalyticsPayload,
  ClubCounterSnapshot,
  ClubMember,
  ClubPaymentAccount,
  ClubPaymentIntent,
  ClubPaymentAccountPayload,
  ClubPaymentEvent,
  ClubPaymentOperationsSummary,
  ClubPaymentValidationResult,
  ClubWithdrawalRequest,
} from '../types';
import { apiRequest } from './apiClient';

const resolveDisplayName = (value: any, fallback = 'Membro'): string =>
  (
    value?.userName ||
    value?.user_name ||
    value?.nickname ||
    [value?.first_name, value?.last_name].filter(Boolean).join(' ').trim() ||
    fallback
  ).trim();

const toOptionalNumber = (value: any): number | undefined => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const toNumberOrZero = (value: any): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeClub = (club: any): Club => ({
  ...club,
  rake_percentage: toNumberOrZero(club?.rake_percentage),
  rake_cap_reais: club?.rake_cap_reais === null || club?.rake_cap_reais === undefined ? null : toOptionalNumber(club?.rake_cap_reais) ?? null,
  total_members: toNumberOrZero(club?.total_members),
  total_tables: toNumberOrZero(club?.total_tables),
  total_rake_earned: toOptionalNumber(club?.total_rake_earned),
});

interface PaginatedClubsResponse {
  total: number;
  page: number;
  limit: number;
  data: Club[];
}

interface ClubMembersResponse {
  total: number;
  data: ClubMember[];
}

interface CounterSnapshotResponse {
  success: boolean;
  data: ClubCounterSnapshot;
}

interface CounterAdjustmentResponse {
  success: boolean;
  data: ClubCounterAdjustmentResult;
}

interface FinanceSummaryResponse {
  success: boolean;
  data: ClubFinanceSummary;
}

interface FinancePlayersResponse {
  success: boolean;
  data: ClubFinancePlayerPosition[];
}

interface FinanceEntriesResponse {
  success: boolean;
  total: number;
  page: number;
  limit: number;
  data: ClubFinanceEntry[];
}

interface FinanceOverviewResponse {
  success: boolean;
  data: ClubFinanceOverview;
}

interface FinancePlayerTrailResponse {
  success: boolean;
  data: ClubFinancePlayerTrail;
}

interface PaymentIntentsResponse {
  success: boolean;
  data: ClubPaymentIntent[];
}

interface FinanceSettlementResponse {
  success: boolean;
  data: ClubFinanceEntry;
}

interface FinanceManualEntryResponse {
  success: boolean;
  data: ClubFinanceEntry;
}

interface ClubSubscriptionResponse {
  success: boolean;
  data: ClubSubscriptionStatus;
}

interface ClubSubscriptionPlansResponse {
  success: boolean;
  data: ClubSubscriptionPlan[];
}

interface ClubSubscriptionContractResponse {
  success: boolean;
  data: ClubSubscriptionContract | null;
}

interface ClubSubscriptionInvoiceResponse {
  success: boolean;
  data: ClubSubscriptionInvoice | null;
}

interface ClubSubscriptionInvoiceListResponse {
  success: boolean;
  data: ClubSubscriptionInvoice[];
}

interface PaymentAccountResponse {
  success: boolean;
  data: {
    account: ClubPaymentAccount | null;
    summary: ClubPaymentOperationsSummary;
  };
}

interface PaymentAccountValidationResponse {
  success: boolean;
  data: ClubPaymentValidationResult;
}

interface PaymentAccountUpsertResponse {
  success: boolean;
  data: ClubPaymentAccount;
}

interface PaymentAccountEventsResponse {
  success: boolean;
  data: ClubPaymentEvent[];
}

interface RakeAnalyticsResponse {
  success: boolean;
  data: ClubRakeAnalyticsPayload;
}

interface AnalyticsOverviewResponse {
  success: boolean;
  data: ClubAnalyticsOverviewPayload;
}

interface WithdrawalRequestsResponse {
  success: boolean;
  data: ClubWithdrawalRequest[];
}

interface WithdrawalRequestResponse {
  success: boolean;
  data: ClubWithdrawalRequest;
}

export const clubService = {
  async listOwnedClubs(ownerId: string): Promise<Club[]> {
    const payload = await apiRequest<PaginatedClubsResponse>(
      `/clubs?page=1&limit=50&ownerId=${encodeURIComponent(ownerId)}`
    );
    return Array.isArray(payload.data) ? payload.data.map(normalizeClub) : [];
  },

  async getClub(clubId: string): Promise<Club> {
    const payload = await apiRequest<Club>(`/clubs/${clubId}`);
    return normalizeClub(payload);
  },

  async getClubMembers(clubId: string): Promise<ClubMember[]> {
    const payload = await apiRequest<ClubMembersResponse>(`/clubs/${clubId}/members`);
    if (!Array.isArray(payload.data)) {
      return [];
    }

    return payload.data.map((member: any) => ({
      userId: member.userId || member.user_id || '',
      userName: resolveDisplayName(member, 'Membro'),
      role: member.role,
      status: member.status,
      joinedAt: member.joinedAt || member.joined_at || '',
    }));
  },

  async getCounterSnapshot(clubId: string): Promise<ClubCounterSnapshot> {
    const payload = await apiRequest<CounterSnapshotResponse>(`/clubs/${clubId}/counter/snapshot`);
    return payload.data;
  },

  async adjustCounter(
    clubId: string,
    data: ClubCounterAdjustmentRequest
  ): Promise<ClubCounterAdjustmentResult> {
    const payload = await apiRequest<CounterAdjustmentResponse>(`/clubs/${clubId}/counter/adjustment`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return payload.data;
  },

  async getFinanceSummary(clubId: string): Promise<ClubFinanceSummary> {
    const payload = await apiRequest<FinanceSummaryResponse>(`/clubs/${clubId}/finance/summary`);
    return payload.data;
  },

  async getFinancePlayers(clubId: string): Promise<ClubFinancePlayerPosition[]> {
    const payload = await apiRequest<FinancePlayersResponse>(`/clubs/${clubId}/finance/players`);
    return Array.isArray(payload.data) ? payload.data : [];
  },

  async getFinanceEntries(
    clubId: string,
    options?: {
      page?: number;
      limit?: number;
      status?: string;
      direction?: string;
      userId?: string;
      from?: string;
      to?: string;
    }
  ): Promise<{ total: number; page: number; limit: number; data: ClubFinanceEntry[] }> {
    const params = new URLSearchParams();
    if (typeof options?.page === 'number') params.set('page', String(options.page));
    if (typeof options?.limit === 'number') params.set('limit', String(options.limit));
    if (options?.status) params.set('status', options.status);
    if (options?.direction) params.set('direction', options.direction);
    if (options?.userId) params.set('userId', options.userId);
    if (options?.from) params.set('from', options.from);
    if (options?.to) params.set('to', options.to);

    const suffix = params.toString() ? `?${params.toString()}` : '';
    const payload = await apiRequest<FinanceEntriesResponse>(`/clubs/${clubId}/finance/entries${suffix}`);
    return {
      total: payload.total || 0,
      page: payload.page || 1,
      limit: payload.limit || 20,
      data: Array.isArray(payload.data) ? payload.data : [],
    };
  },

  async getFinanceOverview(
    clubId: string,
    options?: { from?: string; to?: string; groupBy?: 'day' | 'month' }
  ): Promise<ClubFinanceOverview> {
    const params = new URLSearchParams();
    if (options?.from) params.set('from', options.from);
    if (options?.to) params.set('to', options.to);
    if (options?.groupBy) params.set('groupBy', options.groupBy);

    const suffix = params.toString() ? `?${params.toString()}` : '';
    const payload = await apiRequest<FinanceOverviewResponse>(`/clubs/${clubId}/finance/overview${suffix}`);
    return payload.data;
  },

  async getFinancePlayerTrail(
    clubId: string,
    userId: string,
    options?: {
      status?: string;
      direction?: string;
      from?: string;
      to?: string;
      limit?: number;
    }
  ): Promise<ClubFinancePlayerTrail> {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.direction) params.set('direction', options.direction);
    if (options?.from) params.set('from', options.from);
    if (options?.to) params.set('to', options.to);
    if (typeof options?.limit === 'number') params.set('limit', String(options.limit));

    const suffix = params.toString() ? `?${params.toString()}` : '';
    const payload = await apiRequest<FinancePlayerTrailResponse>(
      `/clubs/${clubId}/finance/players/${userId}/trail${suffix}`
    );
    return payload.data;
  },

  async getClubSubscription(clubId: string): Promise<ClubSubscriptionStatus> {
    const payload = await apiRequest<ClubSubscriptionResponse>(`/clubs/${clubId}/subscription`);
    return {
      ...payload.data,
      history: Array.isArray((payload.data as any)?.history) ? (payload.data as any).history : [],
    };
  },

  async listClubSubscriptionPlans(clubId: string): Promise<ClubSubscriptionPlan[]> {
    const payload = await apiRequest<ClubSubscriptionPlansResponse>(`/clubs/${clubId}/subscription/plans`);
    return Array.isArray(payload.data) ? payload.data : [];
  },

  async createClubSubscriptionContract(
    clubId: string,
    data: {
      planCode: string;
      billingType: ClubSubscriptionBillingType;
      termsAccepted: boolean;
      termsVersion: string;
    }
  ): Promise<ClubSubscriptionContract> {
    const payload = await apiRequest<ClubSubscriptionContractResponse>(`/clubs/${clubId}/subscription/contracts`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!payload.data) {
      throw new Error('Contrato nao retornado pelo backend');
    }
    return payload.data;
  },

  async getCurrentClubSubscriptionContract(clubId: string): Promise<ClubSubscriptionContract | null> {
    const payload = await apiRequest<ClubSubscriptionContractResponse>(
      `/clubs/${clubId}/subscription/contracts/current`
    );
    return payload.data || null;
  },

  async updateCurrentClubSubscriptionContract(
    clubId: string,
    data: {
      planCode: string;
      billingType?: ClubSubscriptionBillingType;
      updatePendingPayments?: boolean;
    }
  ): Promise<ClubSubscriptionContract> {
    const payload = await apiRequest<ClubSubscriptionContractResponse>(
      `/clubs/${clubId}/subscription/contracts/current/update`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );

    if (!payload.data) {
      throw new Error('Contrato nao retornado pelo backend');
    }
    return payload.data;
  },

  async cancelCurrentClubSubscriptionContract(
    clubId: string,
    data?: { reason?: string }
  ): Promise<ClubSubscriptionContract> {
    const payload = await apiRequest<ClubSubscriptionContractResponse>(
      `/clubs/${clubId}/subscription/contracts/current/cancel`,
      {
        method: 'POST',
        body: JSON.stringify(data || {}),
      }
    );

    if (!payload.data) {
      throw new Error('Contrato nao retornado pelo backend');
    }
    return payload.data;
  },

  async resumeClubSubscriptionContract(
    clubId: string,
    data: {
      planCode?: string;
      billingType?: ClubSubscriptionBillingType;
      termsAccepted: boolean;
      termsVersion: string;
    }
  ): Promise<ClubSubscriptionContract> {
    const payload = await apiRequest<ClubSubscriptionContractResponse>(
      `/clubs/${clubId}/subscription/contracts/resume`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );

    if (!payload.data) {
      throw new Error('Contrato nao retornado pelo backend');
    }
    return payload.data;
  },

  async getCurrentClubSubscriptionInvoice(clubId: string): Promise<ClubSubscriptionInvoice | null> {
    const payload = await apiRequest<ClubSubscriptionInvoiceResponse>(
      `/clubs/${clubId}/subscription/invoices/current`
    );
    return payload.data || null;
  },

  async listClubSubscriptionInvoices(clubId: string, limit = 20): Promise<ClubSubscriptionInvoice[]> {
    const payload = await apiRequest<ClubSubscriptionInvoiceListResponse>(
      `/clubs/${clubId}/subscription/invoices?limit=${encodeURIComponent(String(limit))}`
    );
    return Array.isArray(payload.data) ? payload.data : [];
  },

  async getPaymentAccount(
    clubId: string
  ): Promise<{ account: ClubPaymentAccount | null; summary: ClubPaymentOperationsSummary }> {
    const payload = await apiRequest<PaymentAccountResponse>(`/clubs/${clubId}/payment-account`);
    return {
      account: payload.data?.account || null,
      summary: payload.data?.summary || {
        pending_deposits: 0,
        confirmed_deposits: 0,
        wallet_balances_total: 0,
        deposit_gross_total: 0,
        deposit_net_total: 0,
        deposit_fee_total: 0,
        withdrawals_under_review: 0,
        withdrawals_processing: 0,
        withdrawals_paid: 0,
        withdrawal_gross_total: 0,
        withdrawal_net_total: 0,
        withdrawal_fee_total: 0,
        last_webhook_at: null,
        last_error_message: null,
      },
    };
  },

  async listDepositIntents(clubId: string, limit = 40): Promise<ClubPaymentIntent[]> {
    const payload = await apiRequest<PaymentIntentsResponse>(
      `/clubs/${clubId}/payment-intents?limit=${encodeURIComponent(String(limit))}`
    );

    return Array.isArray(payload.data) ? payload.data : [];
  },

  async validatePaymentAccount(
    clubId: string,
    data: Pick<ClubPaymentAccountPayload, 'apiKey' | 'environment' | 'webhookUrl'>
  ): Promise<ClubPaymentValidationResult> {
    const payload = await apiRequest<PaymentAccountValidationResponse>(
      `/clubs/${clubId}/payment-account/validate`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );

    return payload.data;
  },

  async savePaymentAccount(clubId: string, data: ClubPaymentAccountPayload): Promise<ClubPaymentAccount> {
    const payload = await apiRequest<PaymentAccountUpsertResponse>(`/clubs/${clubId}/payment-account`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    return payload.data;
  },

  async getPaymentAccountEvents(clubId: string, limit = 20): Promise<ClubPaymentEvent[]> {
    const payload = await apiRequest<PaymentAccountEventsResponse>(
      `/clubs/${clubId}/payment-account/events?limit=${encodeURIComponent(String(limit))}`
    );

    return Array.isArray(payload.data) ? payload.data : [];
  },

  async settleFinanceEntry(
    clubId: string,
    entryId: string,
    data: {
      amount?: number;
      settlementMethod?: 'PIX' | 'CASH' | 'BANK_TRANSFER' | 'INTERNAL' | 'OTHER';
      note?: string;
    }
  ): Promise<ClubFinanceEntry> {
    const payload = await apiRequest<FinanceSettlementResponse>(
      `/clubs/${clubId}/finance/entries/${entryId}/settlements`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );

    return payload.data;
  },

  async createManualFinanceEntry(
    clubId: string,
    data: ClubFinanceManualEntryRequest
  ): Promise<ClubFinanceEntry> {
    const payload = await apiRequest<FinanceManualEntryResponse>(`/clubs/${clubId}/finance/entries`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    return payload.data;
  },

  async getRakeAnalytics(
    clubId: string,
    options?: { from?: string; to?: string; groupBy?: 'day' | 'week' | 'month'; tableId?: string }
  ): Promise<ClubRakeAnalyticsPayload> {
    const params = new URLSearchParams();
    if (options?.from) params.set('from', options.from);
    if (options?.to) params.set('to', options.to);
    if (options?.groupBy) params.set('groupBy', options.groupBy);
    if (options?.tableId) params.set('tableId', options.tableId);

    const suffix = params.toString() ? `?${params.toString()}` : '';
    const payload = await apiRequest<RakeAnalyticsResponse>(`/clubs/${clubId}/analytics/rake${suffix}`);
    return payload.data;
  },

  async getAnalyticsOverview(
    clubId: string,
    options?: { from?: string; to?: string; groupBy?: 'day' | 'week' | 'month' }
  ): Promise<ClubAnalyticsOverviewPayload> {
    const params = new URLSearchParams();
    if (options?.from) params.set('from', options.from);
    if (options?.to) params.set('to', options.to);
    if (options?.groupBy) params.set('groupBy', options.groupBy);

    const suffix = params.toString() ? `?${params.toString()}` : '';
    const payload = await apiRequest<AnalyticsOverviewResponse>(`/clubs/${clubId}/analytics/overview${suffix}`);
    return payload.data;
  },

  async listWithdrawalRequests(clubId: string, limit = 50): Promise<ClubWithdrawalRequest[]> {
    const payload = await apiRequest<WithdrawalRequestsResponse>(
      `/clubs/${clubId}/withdrawal-requests?limit=${encodeURIComponent(String(limit))}`
    );

    return Array.isArray(payload.data) ? payload.data : [];
  },

  async approveWithdrawalRequest(clubId: string, withdrawalRequestId: string): Promise<ClubWithdrawalRequest> {
    const payload = await apiRequest<WithdrawalRequestResponse>(
      `/clubs/${clubId}/withdrawal-requests/${withdrawalRequestId}/approve`,
      {
        method: 'POST',
      }
    );

    return payload.data;
  },

  async rejectWithdrawalRequest(
    clubId: string,
    withdrawalRequestId: string,
    reason?: string
  ): Promise<ClubWithdrawalRequest> {
    const payload = await apiRequest<WithdrawalRequestResponse>(
      `/clubs/${clubId}/withdrawal-requests/${withdrawalRequestId}/reject`,
      {
        method: 'POST',
        body: JSON.stringify(reason ? { reason } : {}),
      }
    );

    return payload.data;
  },
};
